import React, { useRef, useEffect, useState } from "react";
import rehypeRaw from "rehype-raw";

const MAX_IMAGE_SIZE = 1 * 1024 * 1024;

interface Props {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  image?: File | null;
  onImageChange?: (file: File | null) => void;
  bgColor?: string;
}

export { rehypeRaw };

function isInsideList(div: HTMLDivElement | null): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !div) return false;
  let node: Node | null = sel.getRangeAt(0).startContainer;
  while (node && node !== div) {
    if (node.nodeName === "LI" || node.nodeName === "UL" || node.nodeName === "OL") return true;
    node = node.parentNode;
  }
  return false;
}

export default function RichTextEditor({
  value, onChange, onKeyDown,
  placeholder = "Mesajınızı yazın...",
  disabled = false, minRows = 4,
  image, onImageChange, bgColor,
}: Props) {
  const divRef      = useRef<HTMLDivElement>(null);
  const lastRef     = useRef("");
  const fileRef     = useRef<HTMLInputElement>(null);
  const savedRange  = useRef<Range | null>(null);

  const [imgError, setImgError]     = useState("");
  const [showLink, setShowLink]     = useState(false);
  const [linkUrl, setLinkUrl]       = useState("https://");
  const [linkText, setLinkText]     = useState("");
  const [active, setActive]         = useState<Record<string, boolean>>({});

  // Mount: set initial HTML
  useEffect(() => {
    if (divRef.current) {
      divRef.current.innerHTML = value;
      lastRef.current = value;
    }
  }, []);

  // External reset (e.g. after send)
  useEffect(() => {
    if (divRef.current && value !== lastRef.current) {
      divRef.current.innerHTML = value;
      lastRef.current = value;
    }
  }, [value]);

  const updateActive = () => {
    if (!divRef.current) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const f: Record<string, boolean> = {
      bold:                 document.queryCommandState("bold"),
      italic:               document.queryCommandState("italic"),
      underline:            document.queryCommandState("underline"),
      insertUnorderedList:  document.queryCommandState("insertUnorderedList"),
      insertOrderedList:    document.queryCommandState("insertOrderedList"),
    };
    let node: Node | null = sel.getRangeAt(0).startContainer;
    while (node && node !== divRef.current) {
      const tag = (node as Element).tagName?.toLowerCase();
      if (tag === "s" || tag === "strike") f.strikeThrough = true;
      if (tag === "code")       f.code = true;
      if (tag === "blockquote") f.blockquote = true;
      node = node.parentNode;
    }
    setActive(f);
  };

  const emit = () => {
    if (!divRef.current) return;
    const html = divRef.current.innerHTML;
    const clean = html === "<br>" || html === "<div><br></div>" ? "" : html;
    lastRef.current = clean;
    onChange(clean);
  };

  const execCmd = (cmd: string) => {
    divRef.current?.focus();
    document.execCommand(cmd, false);
    emit();
    setTimeout(updateActive, 0);
  };

  const insertHtml = (html: string) => {
    divRef.current?.focus();
    document.execCommand("insertHTML", false, html);
    emit();
    setTimeout(updateActive, 0);
  };

  const wrapSelection = (open: string, close: string, fallback: string) => {
    const sel = window.getSelection()?.toString();
    // Kapanış etiketinden hemen sonra görünmez bir karakter ekleniyor. Bu olmadan
    // insertHTML imleci eklenen parçanın en sonuna (etiketin İÇİNE) bırakıyor ve
    // yazmaya devam etmek sonsuza kadar aynı biçimde (ör. kod) sürüyor —
    // görünmez karakter imleci etiketin dışına, bir sonraki kardeş düğüme taşıyor.
    insertHtml(`${open}${sel || fallback}${close}​`);
  };

  // ── Link popup ─────────────────────────────────────────────────────────────
  const openLinkPopup = () => {
    // Save current selection before popup steals focus
    const sel = window.getSelection();
    savedRange.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
    setLinkText(sel?.toString() || "");
    setLinkUrl("https://");
    setShowLink(true);
  };

  const confirmLink = () => {
    if (!linkUrl || linkUrl === "https://") { setShowLink(false); return; }
    divRef.current?.focus();
    // Restore saved selection
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
    const text = linkText.trim() || linkUrl;
    insertHtml(`<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`);
    setShowLink(false);
    setLinkUrl("https://");
    setLinkText("");
    savedRange.current = null;
  };
  // ──────────────────────────────────────────────────────────────────────────

  const BTNS = [
    { icon: "bi-type-bold",          title: "Kalın (Ctrl+B)",  key: "bold",                act: () => execCmd("bold") },
    { icon: "bi-type-italic",        title: "İtalik (Ctrl+I)", key: "italic",              act: () => execCmd("italic") },
    { icon: "bi-type-underline",     title: "Altı çizili (Ctrl+U)", key: "underline",      act: () => execCmd("underline") },
    { icon: "bi-type-strikethrough", title: "Üstü çizili",     key: "strikeThrough",       act: () => wrapSelection("<s>", "</s>", "metin") },
    { icon: "bi-code",               title: "Kod",             key: "code",                act: () => wrapSelection("<code>", "</code>", "kod") },
    { icon: "bi-blockquote-left",    title: "Alıntı",          key: "blockquote",          act: () => wrapSelection("<blockquote>", "</blockquote>", "alıntı") },
    { icon: "bi-list-ul",            title: "Bullet liste",    key: "insertUnorderedList", act: () => execCmd("insertUnorderedList") },
    { icon: "bi-list-ol",            title: "Numaralı liste",  key: "insertOrderedList",   act: () => execCmd("insertOrderedList") },
  ];

  const handleImageFile = (file: File | null | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setImgError("Sadece PNG/JPEG."); return; }
    if (file.size > MAX_IMAGE_SIZE)       { setImgError("Resim 1MB'dan büyük olamaz."); return; }
    setImgError("");
    onImageChange?.(file);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey && e.key === "b") { e.preventDefault(); execCmd("bold"); return; }
    if (e.ctrlKey && e.key === "i") { e.preventDefault(); execCmd("italic"); return; }
    if (e.ctrlKey && e.key === "u") { e.preventDefault(); execCmd("underline"); return; }
    if (e.key === "Enter" && !e.ctrlKey) {
      // Liste içindeyse → varsayılan davranış (yeni madde / listeden çık)
      if (isInsideList(divRef.current)) return;
      // Liste dışında → <br> ekle
      e.preventDefault();
      document.execCommand("insertLineBreak");
      emit();
      return;
    }
    onKeyDown?.(e as any);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        e.preventDefault();
        handleImageFile(items[i].getAsFile());
        return;
      }
    }
  };

  const imagePreviewUrl = image ? URL.createObjectURL(image) : null;
  const btnStyle: React.CSSProperties = {
    background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer",
    padding: "4px 7px", borderRadius: 6, color: "#374151",
    fontSize: "0.95rem", display: "flex", alignItems: "center",
  };

  return (
    <>
      <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 14, overflow: "hidden", background: bgColor || "#fff", width: "100%", transition: "background 0.15s" }}>

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1.5px solid #e5e7eb", background: "#f9fafb", padding: "4px 8px", flexWrap: "wrap", gap: 2 }}>
          {BTNS.map((btn) => {
            const isActive = !!active[btn.key];
            return (
              <button key={btn.icon} type="button" title={btn.title} disabled={disabled}
                onMouseDown={(e) => { e.preventDefault(); if (!disabled) btn.act(); }}
                style={{
                  ...btnStyle,
                  background: isActive ? "#e0e7ff" : "none",
                  color: isActive ? "#4f46e5" : "#374151",
                  borderRadius: 6,
                }}
                onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = isActive ? "#c7d2fe" : "#e5e7eb"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = isActive ? "#e0e7ff" : "none"; }}
              >
                <i className={`bi ${btn.icon}`}></i>
              </button>
            );
          })}

          {/* Link butonu */}
          <button type="button" title="Link ekle" disabled={disabled}
            onMouseDown={(e) => { e.preventDefault(); if (!disabled) openLinkPopup(); }}
            style={btnStyle}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#e5e7eb"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
          >
            <i className="bi bi-link-45deg"></i>
          </button>

          {onImageChange && (
            <>
              <div style={{ width: 1, height: 18, background: "#e5e7eb", margin: "0 4px" }} />
              <button type="button" title="Resim ekle (max 1MB)" disabled={disabled || !!image}
                onMouseDown={(e) => { e.preventDefault(); fileRef.current?.click(); }}
                style={{ ...btnStyle, background: image ? "#eef2ff" : "none", color: image ? "#6366f1" : "#374151", cursor: disabled || !!image ? "not-allowed" : "pointer" }}
              >
                <i className="bi bi-image"></i>
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: "none" }}
                onChange={(e) => { handleImageFile(e.target.files?.[0]); e.target.value = ""; }}
              />
            </>
          )}
        </div>

        {/* Editable area */}
        <div style={{ position: "relative" }}>
          <div
            ref={divRef}
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={emit}
            onKeyDown={handleKeyDown}
            onKeyUp={updateActive}
            onMouseUp={updateActive}
            onSelect={updateActive}
            onPaste={handlePaste}
            onDrop={(e) => { e.preventDefault(); handleImageFile(e.dataTransfer.files?.[0]); }}
            style={{
              minHeight: `${minRows * 26}px`,
              padding: "12px 14px",
              fontSize: "0.88rem",
              color: "#374151",
              fontFamily: "inherit",
              outline: "none",
              lineHeight: 1.65,
            }}
          />
          {!value && (
            <div style={{
              position: "absolute", top: "12px", left: "14px",
              color: "#9ca3af", fontSize: "0.88rem",
              pointerEvents: "none", fontStyle: "italic",
            }}>
              {placeholder}
            </div>
          )}
        </div>

        {/* Image preview / error */}
        {(image || imgError) && (
          <div style={{ padding: "8px 14px", borderTop: "1px solid #f3f4f6", background: "#fafafa", display: "flex", alignItems: "center", gap: 10 }}>
            {image && imagePreviewUrl && (
              <>
                <img src={imagePreviewUrl} alt="ek" style={{ height: 44, width: 44, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }} />
                <span style={{ fontSize: "0.78rem", color: "#374151", flex: 1 }}>
                  {image.name} <span style={{ color: "#9ca3af" }}>({(image.size / 1024).toFixed(0)} KB)</span>
                </span>
                <button type="button" onClick={() => { onImageChange?.(null); setImgError(""); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: "0.85rem" }}>
                  <i className="bi bi-x-circle-fill"></i>
                </button>
              </>
            )}
            {imgError && (
              <span style={{ fontSize: "0.78rem", color: "#dc2626" }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>{imgError}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Link Popup ── */}
      {showLink && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowLink(false); }}
        >
          <div style={{ background: "#fff", borderRadius: 16, width: "min(94vw,380px)", boxShadow: "0 8px 40px rgba(0,0,0,0.2)", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#6366f1,#4f46e5)", padding: "14px 18px", color: "#fff", display: "flex", alignItems: "center", gap: 10 }}>
              <i className="bi bi-link-45deg" style={{ fontSize: "1.1rem" }}></i>
              <span style={{ fontWeight: 800, fontSize: "0.95rem" }}>Link Ekle</span>
              <button onClick={() => setShowLink(false)} style={{ marginLeft: "auto", background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 7, color: "#fff", cursor: "pointer", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Link metni */}
              <div>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                  Görünen Metin
                </label>
                <input
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Bağlantı metni (boş bırakılırsa URL gösterilir)"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #e5e7eb", fontSize: "0.88rem", outline: "none", boxSizing: "border-box", color: "#111827" }}
                />
              </div>
              {/* URL */}
              <div>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                  URL <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  autoFocus
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://"
                  onKeyDown={(e) => { if (e.key === "Enter") confirmLink(); if (e.key === "Escape") setShowLink(false); }}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #e5e7eb", fontSize: "0.88rem", outline: "none", boxSizing: "border-box", color: "#111827" }}
                />
              </div>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                <button onClick={() => setShowLink(false)} style={{ padding: "8px 16px", borderRadius: 9, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                  İptal
                </button>
                <button onClick={confirmLink} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="bi bi-check-lg"></i> Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
